import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
  TemplateRef,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TourService } from '@services/tour.service';
import { LoadingPageScreenService } from '@services/loading-page-screen.service';
import { PageViewService } from '../../services/page-view.service';
import { Subject, filter, takeUntil, tap } from 'rxjs';
@Component({
    selector: 'app-page-view-nav',
    templateUrl: './page-view-nav.component.html',
    styleUrls: ['./page-view-nav.component.scss'],
    standalone: false
})
export class PageViewNavComponent implements OnInit, OnChanges, OnDestroy {
  public isHidden: boolean;
  public breadcrumbs: AppPageViewBreadcrumbs[] = [];
  public showLoadingContentOverlay: boolean = false;
  public destroy$ = new Subject();
  @Input() links: AppPageViewLinks[] = [];
  @Input() showSidebar = true;
  @Input() avoidLinksPattern: string[] = []; // Array of patterns that identify segments that should not be links
  @ViewChildren('left-menu-button-admin') adminButton: QueryList<ElementRef>;

  stickyButtonContent: TemplateRef<any> | null = null;
  stickyButtonCallback: Function | null = null;

  constructor(
    private router: Router,
    private tourService: TourService,
    private cd: ChangeDetectorRef,
    private pageViewService: PageViewService,
    private loadingPageScreenService: LoadingPageScreenService
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.drawBreadcrumbs();
      });

    this.pageViewService.stickyButtonContent$
      .pipe(takeUntil(this.destroy$))
      .subscribe(content => {
        this.stickyButtonContent = content;
        this.cd.detectChanges();
      });

    this.loadingPageScreenService.loadingPage$
      .pipe(
        takeUntil(this.destroy$),
        tap(
          (isLoadingPage: boolean) =>
            (this.showLoadingContentOverlay = !!isLoadingPage)
        )
      )
      .subscribe();

    // Draw breadcrumbs on initial load
    this.drawBreadcrumbs();
  }

  ngOnChanges(changes) {
    if (changes && changes?.links && !changes?.links?.firstChange) {
      this.drawBreadcrumbs();
    }
  }

  ngOnDestroy() {
    this.destroy$.next('');
    this.destroy$.complete();
  }

  drawBreadcrumbs() {
    const segments = this.router.url.split(/\//);
    if (!this.links?.length) return;

    this.breadcrumbs = segments.reduce(
      (acc, path, i) => {
        if (!path) return acc;

        path = path.split('?')[0];
        let route = i === 1 ? `/${path}` : `${acc.previousPath}/${path}`;
        acc.previousPath += `/${path}`;

        let text: any = path.split(' ');
        let link = this.links.find(
          link => link.route === path || link.path === path
        );

        text = link?.translation_key
          ? link.translation_key
          : link?.text || text.join('-').split('?')[0];
        if (link) {
          const target = link.directRoute ?? link.route;
          route = target?.startsWith('/') ? target : route;
        }

        if (link?.hideFromBreadcrumb) {
          return acc;
        }
        if (link?.hideFromBreadcrumbWhenExact && i === segments.length - 1) {
          return acc;
        }

        // check if this segment matches any of the avoid link patterns
        const shouldAvoidLink = this.avoidLinksPattern.some(pattern =>
          new RegExp(pattern).test(route)
        );

        acc.breadcrumbs.push({
          ...link,
          link: route,
          text,
          avoidLink: link?.avoidLink || shouldAvoidLink,
        });

        return acc;
      },
      { breadcrumbs: [] as AppPageViewBreadcrumbs[], previousPath: '' }
    ).breadcrumbs;
    this.cd.detectChanges();
  }
  inRoute(route) {
    const regex = new RegExp('/' + route + '(\\?[^/]+)?(?=/|$)');
    if (this.router.url) return this.router.url.split('?')[0].match(regex);
    else return false;
  }

  getNavLinkRoute(link: AppPageViewLinks): string[] | string {
    const raw = link?.directRoute ?? link?.route ?? '';
    if (typeof raw === 'string' && raw.includes('?')) {
      return this.parseBreadcrumbLink(raw).path;
    }
    return raw;
  }

  getNavLinkQueryParams(link: AppPageViewLinks): Record<string, string> | null {
    const raw = link?.directRoute ?? link?.route ?? '';
    if (typeof raw !== 'string' || !raw.includes('?')) return null;
    const params = this.parseBreadcrumbLink(raw).queryParams;
    return Object.keys(params).length ? params : null;
  }

  parseBreadcrumbLink(linkStr: string): { path: string[]; queryParams: Record<string, string> } {
    const [pathPart, queryPart] = (linkStr || '').split('?');
    const path = pathPart.split('/').filter(Boolean);
    if (path.length && pathPart.startsWith('/')) path[0] = '/' + path[0];
    const queryParams: Record<string, string> = {};
    if (queryPart) {
      queryPart.split('&').forEach(pair => {
        const [k, v] = pair.split('=');
        if (k != null && v != null) queryParams[decodeURIComponent(k)] = decodeURIComponent(v);
      });
    }
    return { path, queryParams };
  }
}

export interface AppPageViewBreadcrumbs {
  text: string;
  link: string;
  avoidLink: boolean;
}

export interface AppPageViewLinks {
  text: string;
  route: string;
  translation_key?: string;
  directRoute?: string;
  hidden?: boolean;
  path?: string;
  tourId?: string;
  stepId?: string;
  position?: string;
  avoidLink?: boolean;
  hideFromBreadcrumb?: boolean;
  hideFromBreadcrumbWhenExact?: boolean;
}
